interface FeedbackContext {
  isCorrect: boolean
  confidence: number
  streak: number
  questionNumber: number
  totalQuestions: number
}

/**
 * Growth Mindset Motivational Messages (V5.0 - Expanded Variety)
 *
 * RULES:
 * 1. NEVER use words: "Cito", "toets", "gokken", "fout"
 * 2. ALWAYS validate thinking before correcting
 * 3. Build confidence, don't punish uncertainty
 * 4. Celebrate bravery in making mistakes
 */

export function getMotivationalMessage(context: FeedbackContext): string {
  const { isCorrect, confidence, streak, questionNumber, totalQuestions } =
    context

  if (isCorrect) {
    // Exploring (1-2 stars): Correct but uncertain - validate intuition
    if (confidence <= 2) {
      const messages = [
        'Je gevoel was goed! 🎯',
        'Scherp gezien! Je intuïtie klopt. ✨',
        'Goed gedaan! Je bent op de goede weg. 💫',
        'Precies! Je hebt het door, ook al twijfelde je. 👍',
        'Je instinct was raak! Mooi zo! 🌟',
        'Klopt helemaal! Je voelde het goed aan. 💡',
        'Goed geraden én goed gedacht! 🎲',
        'Je twijfelde, maar je antwoord was perfect! ✓',
        'Soms is twijfelen slim. En je had gelijk! 🧠',
        'Je gevoel loog niet! Goed bezig! 🎊',
      ]
      return messages[Math.floor(Math.random() * messages.length)]
    }

    // Master (5 stars): Perfect understanding
    if (confidence === 5) {
      const messages = [
        'Perfect! Je weet het én je voelt het! 🏆',
        'Lekker bezig! Helemaal goed! 💪',
        'Top! Je bent er helemaal in! 🌟',
        'Geweldig! Dit snap je helemaal! ⚡',
        'Zo kan het ook! Meesterlijk! 👑',
        'Fantastisch! Je hebt dit helemaal onder de knie! 🎯',
        'Briljant! Je bent een ster! ⭐',
        'Wauw! Je weet precies hoe het moet! 🚀',
        'Ongelofelijk goed! Je bent on fire! 🔥',
        'Perfectie! Dit is hoe het hoort! 💎',
        'Schitterend! Je bent een expert! 📚',
        'Magnifiek! Je snapt het helemaal! 🎨',
      ]
      return messages[Math.floor(Math.random() * messages.length)]
    }

    // Developing (3-4 stars): Building confidence
    const messages = [
      'Goed gedaan! Je bent op de goede weg! 👍',
      'Klopt helemaal! Je snapt het steeds beter! 💫',
      'Precies goed! Je groeit! 🌱',
      'Netjes! Je komt er steeds meer in! ✓',
      'Mooi werk! Je bent goed bezig! 🎯',
      'Helemaal goed! Je maakt vooruitgang! 📈',
      'Sterk antwoord! Je ontwikkelt je! 💪',
      'Juist! Je begrijpt het steeds beter! 🧠',
      'Prima! Je bent op de goede weg! 🛤️',
      'Uitstekend! Je groeit elke vraag! 🌟',
    ]
    return messages[Math.floor(Math.random() * messages.length)]
  } else {
    // Brave Learning (4-5 stars): Wrong but confident - celebrate bravery
    if (confidence >= 4) {
      const messages = [
        'Wat goed dat je zo zeker was! Hier zit een doordenker. 💭',
        'Dapper geprobeerd! Dit is een lastige. Laten we hem samen kraken. 🔍',
        'Je dacht goed na! Hier zit een gemene instinker. 🎯',
        'Mooi geprobeerd! Dit is precies waarom we oefenen. 💪',
        'Sterk dat je zo zeker durfde te zijn! Dit is een leermoment. 📚',
        'Je redenering was logisch! Hier zit een trucje. 🧩',
        'Goed nagedacht! Dit is een pittige. Laten we uitzoeken waarom. 🔬',
        'Je was overtuigd! Dit is een belangrijke les. 💡',
        'Moedig antwoord! Hier leer je het meest van. 🎓',
        'Je durfde te kiezen! Dit is hoe je leert. 🌱',
      ]
      return messages[Math.floor(Math.random() * messages.length)]
    }

    // Investigating (1-3 stars): Wrong and uncertain - gentle guidance
    const messages = [
      'Nog niet helemaal. Laten we samen kijken! 🔍',
      'Bijna! Kijk nog eens goed naar de uitleg. 📚',
      'Nog niet helemaal goed. We gaan het samen uitzoeken! 💡',
      'Dit is een leermoment! Laten we het uitpluizen. 🎓',
      'Niet helemaal. De uitleg helpt je verder! 🗺️',
      'Nog even oefenen. Kijk goed naar de stappen! 👣',
      'Bijna goed! Laten we samen kijken waar het zit. 🔎',
      'Dit is lastig! De uitleg maakt het duidelijk. 📖',
      'Nog niet helemaal. Elke vraag is een stap vooruit! 🚶',
      'Niet helemaal juist. Laten we het samen snappen! 🤝',
      'Nog even oefenen! De uitleg laat zien hoe het moet. 🎯',
      'Dit is een uitdaging! Laten we het kraken. 🧩',
    ]
    return messages[Math.floor(Math.random() * messages.length)]
  }
}

/**
 * Get confidence-based feedback (Growth Mindset)
 */
export function getConfidenceFeedback(
  isCorrect: boolean,
  confidence: number,
): string {
  if (isCorrect && confidence <= 2) {
    return 'Je intuïtie was goed! Laten we kijken waarom je gevoel klopte.'
  }

  if (!isCorrect && confidence >= 4) {
    return 'Wat goed dat je zo zeker durfde te zijn! Dit is een belangrijke doordenker.'
  }

  return ''
}

/**
 * Get checkpoint message based on performance
 */
export function getCheckpointMessage(
  correctCount: number,
  totalCount: number,
): string {
  const percentage = (correctCount / totalCount) * 100

  if (percentage === 100) {
    return 'Alles goed! Je bent on fire! 🔥'
  } else if (percentage >= 80) {
    return 'Geweldig! Je bent goed bezig! 🌟'
  } else if (percentage >= 60) {
    return 'Mooi! Je groeit elke vraag! 💪'
  } else if (percentage >= 40) {
    return 'Je leert veel! Blijf doorgaan! 📈'
  } else {
    return 'Elke vraag is een stap vooruit! 🎯'
  }
}

/**
 * Get level-up title based on level
 */
export function getLevelTitle(level: number): string {
  if (level === 1) return 'Beginner'
  if (level === 2) return 'Verkenner'
  if (level === 3) return 'Kenner'
  if (level === 4) return 'Expert'
  if (level >= 5) return 'Meester'
  return 'Leerling'
}

/**
 * Get level-up message
 */
export function getLevelUpMessage(level: number): string {
  const messages: Record<number, string> = {
    2: 'Je groeit! Tijd voor nieuwe uitdagingen! 🚀',
    3: 'Wow! Je wordt steeds beter! 👑',
    4: 'Indrukwekkend! Je bent een echte expert! 📚',
    5: 'Legendarisch! Je bent een meester! ⭐',
  }

  return messages[level] || 'Je bent weer een level omhoog! Top! 🎉'
}

/**
 * Get streak celebration message
 */
export function getStreakMessage(streak: number): string {
  if (streak === 3) {
    return '🔥 3 op rij! Je bent lekker bezig!'
  } else if (streak === 5) {
    return '🔥 5 op rij! Je bent on fire!'
  } else if (streak === 7) {
    return '⚡ 7 op rij! Ongelofelijk!'
  } else if (streak === 10) {
    return '⚡ 10 op rij! Fenomenaal!'
  } else if (streak === 15) {
    return '🌟 15 op rij! Je bent een legende!'
  } else if (streak === 20) {
    return '👑 20 op rij! Meesterlijk!'
  } else if (streak % 5 === 0 && streak > 0) {
    return `🎯 ${streak} op rij! Blijf zo doorgaan!`
  }
  return ''
}
